import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { toast } from "sonner";
import { Loader2, Receipt, Plus, X } from "lucide-react";

const emptyForm = () => ({ scope_text: "", deliverables: "", turnaround_text: "", currency: "INR", valid_until: "", notes_from_njm: "", line_items: [{ label: "", amount: "" }] });

const toFormFromQuote = (q) => ({
  scope_text: q.scope_text || "", deliverables: (q.deliverables || []).join(", "), turnaround_text: q.turnaround_text || "",
  currency: q.currency || "INR", valid_until: q.valid_until || "", notes_from_njm: q.notes_from_njm || "",
  line_items: q.line_items?.length ? q.line_items.map((li) => ({ label: li.label, amount: String(li.amount) })) : [{ label: "", amount: "" }],
});

const toBody = (form) => ({
  scope_text: form.scope_text, turnaround_text: form.turnaround_text, currency: form.currency, valid_until: form.valid_until,
  notes_from_njm: form.notes_from_njm,
  deliverables: form.deliverables.split(",").map((d) => d.trim()).filter(Boolean),
  line_items: form.line_items.filter((li) => li.label.trim()).map((li) => ({ label: li.label, amount: parseFloat(li.amount) || 0 })),
});

/** Edit Requests Phase 1 (2026-09) — staff half of the "Edit Quote" negotiation loop: compose a
 * Draft, send it, or issue a revision on the active version. The client-facing approve/
 * request-changes half is Phase 2. Deliberately labeled "Edit Quote" everywhere — never bare
 * "Quote" — per the founder's naming decision (this sits in the same queue as, but is
 * unrelated to, Commercial Engine quotes). */
export const EditQuotePanel = ({ requestId }) => {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setErrored(false);
    return api.get(`/v1/client-project-requests/${requestId}/edit-quotes/current`)
      .then((r) => setQuote(r.data.quote))
      .catch(() => setErrored(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [requestId]); // eslint-disable-line react-hooks/exhaustive-deps

  const addLine = () => setForm({ ...form, line_items: [...form.line_items, { label: "", amount: "" }] });
  const removeLine = (i) => setForm({ ...form, line_items: form.line_items.filter((_, idx) => idx !== i) });
  const updateLine = (i, key, val) => setForm({ ...form, line_items: form.line_items.map((li, idx) => idx === i ? { ...li, [key]: val } : li) });

  const compose = async () => {
    if (!form.scope_text.trim()) { toast.error("Scope is required"); return; }
    setSaving(true);
    try {
      const r = await api.post(`/v1/client-project-requests/${requestId}/edit-quotes`, toBody(form));
      setQuote(r.data.quote);
      setShowForm(false);
      toast.success("Edit Quote drafted");
    } catch { toast.error("Couldn't draft the Edit Quote"); } finally { setSaving(false); }
  };

  const send = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/v1/client-project-requests/${requestId}/edit-quotes/${quote.id}/send`);
      setQuote(r.data.quote);
      toast.success("Edit Quote sent to client");
    } catch { toast.error("Couldn't send the Edit Quote"); } finally { setSaving(false); }
  };

  const issueRevision = async () => {
    if (!form.scope_text.trim()) { toast.error("Scope is required"); return; }
    setSaving(true);
    try {
      const r = await api.post(`/v1/client-project-requests/${requestId}/edit-quotes/${quote.id}/issue-revision`, toBody(form));
      setQuote(r.data.quote);
      setShowForm(false);
      toast.success("Revised Edit Quote sent to client");
    } catch { toast.error("Couldn't issue a revision"); } finally { setSaving(false); }
  };

  const openRevisionForm = () => { setForm(toFormFromQuote(quote)); setShowForm(true); };

  if (loading) return null;

  if (errored) {
    return <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600" data-testid={`edit-quote-panel-error-${requestId}`}>Couldn't load the Edit Quote</div>;
  }

  return (
    <div className="rounded-md border border-[#E7E5E4] bg-[#FAFAF9]" data-testid={`edit-quote-panel-${requestId}`}>
      <header className="px-3 py-2 flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wider text-[#57534E] inline-flex items-center gap-1"><Receipt className="h-3 w-3" /> Edit Quote</span>
        {quote?.status === "Sent" && !showForm && (
          <button onClick={openRevisionForm} data-testid={`edit-quote-revise-btn-${requestId}`} className="text-[11px] uppercase tracking-wider text-[#57534E] hover:text-[#B45309]">Issue Revision</button>
        )}
      </header>

      {!quote && !showForm && (
        <div className="px-3 pb-3">
          <button onClick={() => { setForm(emptyForm()); setShowForm(true); }} data-testid={`edit-quote-compose-btn-${requestId}`}
            className="text-[11px] uppercase tracking-wider text-[#57534E] hover:text-[#B45309]">Compose Edit Quote</button>
        </div>
      )}

      {quote && !showForm && (
        <div className="px-3 pb-3 text-xs space-y-1" data-testid={`edit-quote-summary-${requestId}`}>
          <div className="text-[11px] uppercase tracking-wider text-[#57534E]">v{quote.version} · {quote.status}</div>
          <p className="text-[#1C1C1E] line-clamp-2">{quote.scope_text}</p>
          <div className="text-[#57534E]">{quote.currency} {quote.total} · valid until {quote.valid_until || "—"}</div>
          {quote.status === "Draft" && (
            <button onClick={send} disabled={saving} data-testid={`edit-quote-send-btn-${requestId}`}
              className="mt-1 inline-flex items-center gap-1.5 rounded-md bg-[#1C1C1E] px-2.5 py-1 text-xs text-white hover:bg-[#B45309] disabled:opacity-50">
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : "Send to client"}
            </button>
          )}
        </div>
      )}

      {showForm && (
        <div className="px-3 pb-3 space-y-1.5">
          <textarea data-testid={`edit-quote-scope-input-${requestId}`} value={form.scope_text} onChange={(e) => setForm({ ...form, scope_text: e.target.value })}
            placeholder="Scope of the edit" rows={2} className="w-full rounded-md border border-[#E7E5E4] px-2 py-1.5 text-xs focus:outline-none focus:border-[#FFC817]/50" />
          <input data-testid={`edit-quote-deliverables-input-${requestId}`} value={form.deliverables} onChange={(e) => setForm({ ...form, deliverables: e.target.value })}
            placeholder="Deliverables (comma-separated)" className="w-full rounded-md border border-[#E7E5E4] px-2 py-1.5 text-xs focus:outline-none focus:border-[#FFC817]/50" />
          <input data-testid={`edit-quote-turnaround-input-${requestId}`} value={form.turnaround_text} onChange={(e) => setForm({ ...form, turnaround_text: e.target.value })}
            placeholder="Turnaround (e.g. 5 working days)" className="w-full rounded-md border border-[#E7E5E4] px-2 py-1.5 text-xs focus:outline-none focus:border-[#FFC817]/50" />
          <div className="space-y-1">
            {form.line_items.map((li, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <input data-testid={`edit-quote-line-label-${requestId}-${i}`} value={li.label} onChange={(e) => updateLine(i, "label", e.target.value)}
                  placeholder="Line item" className="flex-1 rounded-md border border-[#E7E5E4] px-2 py-1 text-xs focus:outline-none focus:border-[#FFC817]/50" />
                <input data-testid={`edit-quote-line-amount-${requestId}-${i}`} value={li.amount} onChange={(e) => updateLine(i, "amount", e.target.value)}
                  placeholder="Amount" type="number" className="w-24 rounded-md border border-[#E7E5E4] px-2 py-1 text-xs focus:outline-none focus:border-[#FFC817]/50" />
                <button onClick={() => removeLine(i)} data-testid={`edit-quote-line-remove-${requestId}-${i}`} className="text-[#A8A29E] hover:text-[#B45309]"><X className="h-3 w-3" /></button>
              </div>
            ))}
            <button onClick={addLine} data-testid={`edit-quote-line-add-${requestId}`} className="inline-flex items-center gap-1 text-[11px] text-[#57534E] hover:text-[#B45309]"><Plus className="h-3 w-3" /> Add line item</button>
          </div>
          <input data-testid={`edit-quote-valid-until-input-${requestId}`} value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
            placeholder="Valid until (YYYY-MM-DD)" className="w-full rounded-md border border-[#E7E5E4] px-2 py-1.5 text-xs focus:outline-none focus:border-[#FFC817]/50" />
          <div className="flex items-center gap-2 pt-1">
            <button onClick={quote ? issueRevision : compose} disabled={saving} data-testid={`edit-quote-save-btn-${requestId}`}
              className="inline-flex items-center gap-1.5 rounded-md bg-[#1C1C1E] px-2.5 py-1 text-xs text-white hover:bg-[#B45309] disabled:opacity-50">
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : quote ? "Send revision" : "Save as Draft"}
            </button>
            <button onClick={() => setShowForm(false)} data-testid={`edit-quote-cancel-btn-${requestId}`} className="text-xs text-[#A8A29E] hover:text-[#1C1C1E]">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};
